import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NumberRequestsAPI } from "@/features/number-requests/api/number-requests";
import type { CompanyLookup } from "@/features/number-requests/types";
import { useTranslation } from "@/features/utils/i18n";
import { formatFileSize } from "@/features/utils/numbers";
import {
  CheckCircleIcon,
  GlobeIcon,
  InfoIcon,
  UploadSimpleIcon,
} from "@phosphor-icons/react";
import { useMutation } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useParams } from "react-router-dom";

/** Scans and photos — what people actually have for these papers. */
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.heic";

/**
 * Who the company is: a French company gives its SIREN and the directory says
 * the rest; any other gives its name and its registration document, which is
 * what we file instead.
 */
export type CompanyAnswer =
  | { mode: "siren"; company: CompanyLookup }
  | { mode: "foreign"; name: string; file: File };

export function CompanyStep({
  value,
  onChange,
}: {
  value: CompanyAnswer | null;
  onChange: (value: CompanyAnswer | null) => void;
}) {
  const { t } = useTranslation("numberRequests");
  const { company: clientId } = useParams<{ company: string }>();

  const [foreign, setForeign] = useState(value?.mode === "foreign");
  const [siren, setSiren] = useState(
    value?.mode === "siren" ? value.company.siren : "",
  );
  const [refusal, setRefusal] = useState<"invalid" | "not_found" | null>(null);
  const [name, setName] = useState(value?.mode === "foreign" ? value.name : "");
  const [file, setFile] = useState<File | null>(
    value?.mode === "foreign" ? value.file : null,
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const lookup = useMutation({
    mutationFn: (siren: string) =>
      NumberRequestsAPI.lookupCompany(clientId || "", siren),
  });

  const check = async (event: React.FormEvent) => {
    event.preventDefault();
    setRefusal(null);
    onChange(null);
    const company = await lookup.mutateAsync(siren).catch(() => null);
    if (!company) return;
    if (company.check === "invalid" || company.check === "not_found") {
      setRefusal(company.check);
      return;
    }
    onChange({ mode: "siren", company });
  };

  const setForeignAnswer = (next: { name?: string; file?: File | null }) => {
    const nextName = next.name ?? name;
    const nextFile = next.file === undefined ? file : next.file;
    setName(nextName);
    setFile(nextFile);
    onChange(
      nextName.trim() && nextFile
        ? { mode: "foreign", name: nextName.trim(), file: nextFile }
        : null,
    );
  };

  if (foreign) {
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="company-name">
            {t("numberRequests.create.company.nameLabel")}
          </Label>
          <Input
            id="company-name"
            value={name}
            maxLength={200}
            onChange={(event) => setForeignAnswer({ name: event.target.value })}
            placeholder={t("numberRequests.create.company.namePlaceholder")}
          />
        </div>

        <div className="space-y-2">
          <Label>{t("numberRequests.create.company.documentLabel")}</Label>
          <p className="text-muted-foreground text-sm">
            {t("numberRequests.create.company.documentHelp")}
          </p>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(event) => {
              const picked = event.target.files?.[0];
              if (picked) setForeignAnswer({ file: picked });
              event.target.value = "";
            }}
          />
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
            >
              <UploadSimpleIcon className="h-4 w-4" />
              {file
                ? t("numberRequests.documents.replace")
                : t("numberRequests.documents.add")}
            </Button>
            {file && (
              <span className="text-muted-foreground min-w-0 truncate text-sm">
                {file.name} · {formatFileSize(file.size)}
              </span>
            )}
          </div>
          <p className="text-muted-foreground text-xs">
            {t("numberRequests.documents.formats")}
          </p>
        </div>

        <Button
          type="button"
          variant="link"
          className="h-auto p-0"
          onClick={() => {
            setForeign(false);
            onChange(null);
          }}
        >
          {t("numberRequests.create.company.backToSiren")}
        </Button>
      </div>
    );
  }

  const found = value?.mode === "siren" ? value.company : null;

  return (
    <div className="space-y-4">
      <form onSubmit={check} className="space-y-2">
        <Label htmlFor="company-siren">
          {t("numberRequests.create.company.sirenLabel")}
        </Label>
        <div className="flex gap-2">
          <Input
            id="company-siren"
            value={siren}
            inputMode="numeric"
            autoComplete="off"
            onChange={(event) => {
              setSiren(event.target.value.replace(/[^\d\s]/g, "").slice(0, 11));
              setRefusal(null);
              if (value) onChange(null);
            }}
            placeholder="123 456 789"
            className="tabular-nums"
          />
          <Button
            type="submit"
            variant="outline"
            disabled={lookup.isPending || siren.replace(/\D/g, "").length !== 9}
          >
            {lookup.isPending
              ? t("numberRequests.create.company.checking")
              : t("numberRequests.create.company.check")}
          </Button>
        </div>
        {refusal && (
          <p className="text-destructive text-sm">
            {t(`numberRequests.create.company.${refusal}`)}
          </p>
        )}
        {lookup.isError && (
          <p className="text-destructive text-sm">
            {t("numberRequests.create.company.lookupFailed")}
          </p>
        )}
      </form>

      {found && (
        <div className="bg-muted/50 flex items-start gap-3 rounded-lg border p-3">
          {found.check === "verified" ? (
            <CheckCircleIcon
              className="text-primary mt-0.5 h-5 w-5 shrink-0"
              weight="fill"
            />
          ) : (
            <InfoIcon className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0" />
          )}
          <div className="min-w-0 space-y-0.5 text-sm">
            {found.check === "verified" ? (
              <>
                <p className="font-medium">{found.name || found.siren}</p>
                {found.address && (
                  <p className="text-muted-foreground">{found.address}</p>
                )}
              </>
            ) : (
              <p className="text-muted-foreground">
                {t("numberRequests.create.company.unverified")}
              </p>
            )}
          </div>
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={() => {
          setForeign(true);
          setForeignAnswer({});
        }}
      >
        <GlobeIcon className="h-4 w-4" />
        {t("numberRequests.create.company.foreign")}
      </Button>
    </div>
  );
}
